import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { useAtom, useSetAtom } from 'jotai';
import { RADIO_ALT_LOGOS } from '@/config/constants';
import Close from '@/components/Icons/Close';
import PlayIcon from '@/components/Icons/PlayIcon';
import PauseIcon from '@/components/Icons/PauseIcon';
import Backward10Icon from '@/components/Icons/Backward10Icon';
import Forward10Icon from '@/components/Icons/Forward10Icon';
import LiveRadioPill from '@/components/Icons/LiveRadioPill';
import MiniWindowIcon from '@/components/Icons/MiniWindowIcon';
import BottomBarWindowIcon from '@/components/Icons/BottomBarWindowIcon';
import VolumeOnIcon from '@/components/Icons/VolumeOnIcon';
import VolumeOffIcon from '@/components/Icons/VolumeOffIcon';
import ScheduleCard from '@/components/Radio/ScheduleCard';
import VolumeMenu from '@/components/Radio/VolumeMenu';
import ProgressBar from '@/components/common/ProgressBar.client';
import ThumbCard from '@/components/common/ThumbCard';
import {
  activeRadioAtom,
  activeAudioAtom,
  fixedRadioPlayerAtom,
  volumeAtom,
  isMutedAtom,
  isPlayingRadioAtom,
} from '@/store';
import { trailString } from '@/utils/strings';
import MouseMoveHandler from '@/components/common/MouseMoveHandler';

const RadioPlayer: React.FC = () => {
  const [volume, setVolume] = useAtom(volumeAtom);
  const setActiveRadio = useSetAtom(activeRadioAtom);
  const [activeAudio, setActiveAudio] = useAtom(activeAudioAtom);
  const [isPlayingRadio, setIsPlayingRadio] = useAtom(isPlayingRadioAtom);
  const [fixedRadioPlayer] = useAtom(fixedRadioPlayerAtom);
  const [isMuted, setIsMuted] = useAtom(isMutedAtom);
  const [progress, setProgress] = useState<[number, number]>([1, 1]);
  const [progressSeconds, setProgressSeconds] = useState<[number, number]>([0, 0]);
  const [isLive, setIsLive] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [seek, setSeek] = useState(0);
  const [isMiniWindow, setIsMiniWindow] = useState(false);
  const [miniPlayerPosition, setMiniPlayerPosition] = useState([16, 16]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragInitialPosition, setDragInitialPosition] = useState([0, 0]);
  const [dragPosition, setDragPosition] = useState([0, 0]);

  const imagePath = activeAudio ? RADIO_ALT_LOGOS[activeAudio.stationId] : '';

  const playerRef = React.useRef<ReactPlayer>(null);

  const handleClose = () => {
    setActiveRadio(null);
    setActiveAudio(null);
  };

  const handleTogglePlayer = () => {
    setIsPlayingRadio(!isPlayingRadio);
  };

  const setSeeker = (seekTime: number) => {
    if (!playerRef.current) {
      return;
    }
    playerRef.current.seekTo(Math.max(seekTime));
  };

  const handleMutedToggle = () => {
    setIsMuted(!isMuted);
  };

  const skip = (seconds: number) => {
    if (!playerRef.current) {
      return;
    }
    setSeeker(playerRef.current.getCurrentTime() + seconds);
  };

  const skipBack = () => {
    skip(-10);
  };

  const skipForward = () => {
    skip(10);
  };

  const handleSeekChange = (percentage: number) => {
    if (!playerRef.current) {
      return;
    }
    const totalSeconds = activeAudio?.isLiveFeed ? playerRef.current.getSecondsLoaded() : activeAudio?.duration || 0;
    const offsetTotalSeconds = (totalSeconds * percentage) / 100;
    setSeeker(offsetTotalSeconds);
    setSeek(percentage);
  };

  const goLive = () => {
    if (!playerRef.current || !activeAudio?.isLiveFeed) {
      return;
    }
    playerRef.current.seekTo(Math.max(playerRef.current.getDuration() - 10, 1)); // safety offset
    setIsLive(true);
  };

  const updateProgress = () => {
    if (!playerRef.current) {
      return;
    }
    if (activeAudio?.isLiveFeed) {
      // If almost að full progress or less than 10 seconds from full progress
      if (progress[0] >= 0.99 || progressSeconds[1] - progressSeconds[0] < 20) {
        setIsLive(true);
        setSeek(100);
      } else {
        setIsLive(false);
        setSeek((100 * progress[0]) / progress[1]);
      }
    } else {
      const duration = activeAudio?.duration || 0.001;
      setSeek((100 * progressSeconds[0]) / duration);
    }
  };

  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (fixedRadioPlayer) {
      return;
    }
    setIsDragging(true);
    setDragInitialPosition([event.clientX, event.clientY]);
  };

  const onMouseUp = () => {
    setIsDragging(false);
    setDragInitialPosition([0, 0]);
    setMiniPlayerPosition([miniPlayerPosition[0] - dragPosition[0], miniPlayerPosition[1] - dragPosition[1]]);
    setDragPosition([0, 0]);
  };

  const onMouseMove = (event: MouseEvent) => {
    if (!isDragging || fixedRadioPlayer) {
      return;
    }
    setIsDragging(true);
    setDragPosition([event.clientX - dragInitialPosition[0], event.clientY - dragInitialPosition[1]]);
  };

  const onVolumeChange = (volume: number) => {
    setVolume(volume);
    setIsMuted(false);
  };

  useEffect(() => {
    const handleResize = () => {
      if (isMiniWindow && window.innerWidth <= 768) {
        setIsMiniWindow(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMiniWindow]);

  return (
    <>
      {activeAudio && (
        <MouseMoveHandler onMove={onMouseMove}>
          <div
            className={`fixed bottom-16 z-50 ${isMiniWindow ? 'w-fit' : 'w-full'}`}
            style={
              isMiniWindow
                ? {
                    right: `${miniPlayerPosition[0] - dragPosition[0]}px`,
                    bottom: `${miniPlayerPosition[1] - dragPosition[1]}px`,
                  }
                : undefined
            }
          >
            <div
              className={`col-span-full mx-56 my-16 ${
                isMiniWindow ? (isDragging ? 'w-96 cursor-grabbing' : 'w-96 cursor-grab') : 'bottom-8 col-span-full'
              }`}
              //{`flex flex-row fixed z-50 ${isMiniWindow ? (isDragging ? 'cursor-grabbing w-96' : 'cursor-grab w-96') : 'col-span-full bottom-8'}`}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
              // onMouseMove={onMouseMove}
              // onMouseLeave={onMouseUp}
            >
              <div
                className={`relative flex w-full items-center rounded-32 bg-white-100 p-12 shadow-md md:p-16 ${
                  isMiniWindow ? 'flex-col' : 'flex-row'
                }`}
              >
                <div className={`${!isMiniWindow ? 'collapse hidden' : 'absolute'} start-4 top-4 z-20`}>
                  <button onClick={() => setIsMiniWindow(false)} className={'rounded-full p-8 hover:bg-black-25'}>
                    <BottomBarWindowIcon height={'20'} width={'21'} />
                  </button>
                </div>
                <div className={'absolute end-8 top-8 md:end-8 md:top-8'}>
                  <button
                    onClick={handleClose}
                    className={
                      'bg-white border-theme-gray-mid-light rounded-full border p-1 hover:bg-black-25 md:border-0 md:p-2 md:hover:bg-black-25 '
                    }
                  >
                    <Close height={'16'} width={'16'} />
                  </button>
                </div>
                <div
                  className={`relative flex w-full ${isMiniWindow ? 'flex-col' : 'flex-col md:flex-row'} items-center`}
                >
                  <div className={`order-1 flex min-w-fit select-none flex-row items-center self-start md:self-center`}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imagePath}
                      alt={activeAudio.description}
                      className={'pointer-events-none mr-2 w-100 select-none md:w-128 lg:mr-4'}
                    />
                    {activeAudio.isLiveFeed ? (
                      <ScheduleCard hideImage={isMiniWindow} playingAt={currentTime} />
                    ) : (
                      <ThumbCard
                        title={trailString(activeAudio.title || '', 24, 20)}
                        description={trailString(activeAudio.description || '', 20, 18)}
                        photo={`https://www.visir.is/mi/300x300${activeAudio.mainthumb}`}
                      />
                    )}
                  </div>
                  <div
                    className={`col-auto mx-8 flex flex-row justify-between lg:mx-4 ${
                      isMiniWindow ? 'order-3 w-full justify-evenly' : 'xl:w-120 order-3 md:order-2'
                    }`}
                  >
                    <button className={`rounded-full ${isMiniWindow ? 'mr-8' : 'collapse hidden'}`} onClick={goLive}>
                      <LiveRadioPill isActive={isLive} isSmall={true} />
                    </button>
                    <button
                      className={'rounded-full p-8 hover:bg-black-25'}
                      onClick={skipBack}
                      style={{ width: '40px', height: '40px' }}
                    >
                      <Backward10Icon strokeColor={'#012447'} />
                    </button>
                    <button
                      className={'rounded-full p-8 hover:bg-black-25'}
                      onClick={handleTogglePlayer}
                      style={{ width: '40px', height: '40px' }}
                    >
                      {isPlayingRadio ? (
                        <PauseIcon width={'24'} height={'24'} strokeColor={'#012447'} />
                      ) : (
                        <PlayIcon width={'24'} strokeColor={'#012447'} />
                      )}
                    </button>
                    <button
                      className={'rounded-full p-8 hover:bg-black-25'}
                      onClick={skipForward}
                      style={{ width: '40px', height: '40px' }}
                    >
                      <Forward10Icon strokeColor={'#012447'} />
                    </button>
                    <div className={isMiniWindow ? '' : 'xl:collapse xl:hidden'}>
                      <VolumeMenu>
                        {isMuted ? (
                          <VolumeOffIcon width="24" height="24" strokeColor={'#012447'} />
                        ) : (
                          <VolumeOnIcon width="24" height="24" strokeColor={'#012447'} />
                        )}
                      </VolumeMenu>
                    </div>
                  </div>
                  <div
                    className={`m-8 my-8 flex w-full select-none flex-row items-center ${
                      isMiniWindow ? 'order-2 my-8' : 'order-8 md:order-3 md:my-0'
                    }`}
                  >
                    <ProgressBar strokeColor={'primary-light-coral'} progress={seek} onChange={handleSeekChange} />
                    <span className={'text-12 ml-8'}>
                      {activeAudio?.isLiveFeed
                        ? new Date(currentTime).toTimeString().substring(0, isMiniWindow ? 5 : 8)
                        : `-${new Date((activeAudio?.duration || 0) * 1000 - currentTime)
                            .toTimeString()
                            .substring(0, isMiniWindow ? 5 : 8)}`}
                    </span>
                  </div>
                  <div
                    className={`mx-8 items-center ${
                      isMiniWindow ? 'collapse hidden' : 'order-4 hidden w-[240px] flex-row xl:flex'
                    }`}
                  >
                    <button className="mr-8 rounded-full p-8 hover:bg-black-25" onClick={handleMutedToggle}>
                      {isMuted ? <VolumeOffIcon strokeColor={'#012447'} /> : <VolumeOnIcon strokeColor={'#012447'} />}
                    </button>
                    <ProgressBar progress={volume} strokeColor={'primary-light-coral'} onChange={onVolumeChange} />
                  </div>
                  <div
                    className={`items-center md:mr-2 ${
                      isMiniWindow || !activeAudio?.isLiveFeed
                        ? 'collapse hidden'
                        : 'absolute end-0 top-4 order-none md:static md:order-5'
                    }`}
                  >
                    <button className={`rounded-full`} onClick={goLive}>
                      <LiveRadioPill isActive={isLive} />
                    </button>
                  </div>
                  <div
                    className={`hidden items-center ${
                      isMiniWindow ? 'collapse hidden' : 'order-6 flex flex-row md:block'
                    }`}
                  >
                    <button className={'rounded-full p-8 hover:bg-black-25'} onClick={() => setIsMiniWindow(true)}>
                      <MiniWindowIcon width="25" height="24" />
                    </button>
                  </div>
                </div>
                <span className="order-7 flex flex-row">{activeAudio?.isLiveFeed}</span>
                <ReactPlayer
                  ref={playerRef}
                  playing={isPlayingRadio}
                  volume={volume / 100}
                  controls={false}
                  width={0}
                  height={0}
                  url={`${activeAudio.url}${activeAudio.isLiveFeed ? '?DVR' : ''}`}
                  muted={isMuted}
                  onPause={() => setIsPlayingRadio(false)}
                  onPlay={() => setIsPlayingRadio(true)}
                  onReady={() => setIsLoading(true)}
                  config={{
                    file: { forceAudio: true },
                  }}
                  onDuration={(duration) => {
                    if (activeAudio?.isLiveFeed) {
                      setStartTime(Date.now() - duration * 1000);
                    } else {
                      setStartTime(duration);
                    }
                  }}
                  onProgress={(x) => {
                    if (x.loaded && isLoading) {
                      if (activeAudio?.isLiveFeed) {
                        goLive();
                      }
                      setIsLoading(false);
                    }
                    setProgress([x.played, x.loaded]);
                    setProgressSeconds([x.playedSeconds, x.loadedSeconds]);
                    setCurrentTime(startTime + x.playedSeconds * 1000);
                    updateProgress();
                  }}
                />
              </div>
            </div>
          </div>
        </MouseMoveHandler>
      )}
    </>
  );
};

export default RadioPlayer;
